// SecretDialog — the show-once reveal after minting a credential (ADR-004: the raw
// secret is never persisted, so this is the ONE time it is visible). Promoted to a
// shared pattern by its 2nd consumer (personal access tokens; 1st: api_clients).
//
// Text-agnostic — the consumer passes the wording from its own i18n namespace (the
// component owns the SHAPE: a warning Alert + a masked CopyField + one acknowledge
// button; not the copy). A plain Modal (not ConfirmDialog — it is display +
// acknowledge, not a decision).

import type { ReactNode } from 'react';
import { Modal } from '../ui/Modal.js';
import { Button } from '../ui/Button.js';
import { CopyField } from '../ui/CopyField.js';
import { Alert } from '../ui/Alert.js';

interface SecretDialogProps {
  open: boolean;
  /** The raw secret — shown masked-by-default in the CopyField, revealable + copyable. */
  secret: string;
  onClose: () => void;
  /** Dialog title (e.g. "Token created"). */
  title: string;
  /** The "store it now, it won't be shown again" warning. */
  warning: ReactNode;
  /** Label above the secret field. */
  fieldLabel: string;
  /** The single acknowledge button ("I've stored it"). */
  doneLabel: string;
}

export function SecretDialog({ open, secret, onClose, title, warning, fieldLabel, doneLabel }: SecretDialogProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      footer={
        <Button variant="primary" onClick={onClose}>
          {doneLabel}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <Alert variant="warning">{warning}</Alert>
        <CopyField label={fieldLabel} value={secret} secret />
      </div>
    </Modal>
  );
}
